import { CssBaseline, createTheme, ThemeProvider } from '@mui/material'
import { QueryClient } from '@tanstack/react-query'
import polyglotI18nProvider from 'ra-i18n-polyglot'
import { useState } from 'react'
import {
  Admin,
  CustomRoutes,
  I18nContextProvider,
  localStorageStore,
  Resource,
  StoreContextProvider,
} from 'react-admin'
import { Route } from 'react-router'
import activities from './activities'
import ForgotPassword from './auth/ForgotPassword'
import ResetPassword from './auth/ResetPassword'
import SsoLanding from './auth/SsoLanding'
import VerifyEmail from './auth/VerifyEmail'
import authProvider from './authProvider'
import categories from './categories'
import clinicTeam from './clinic-team'
import AcceptTeamInvite from './clinic-team/AcceptTeamInvite'
import { ConsentRequests } from './consent'
import { Dashboard } from './dashboard'
import dataProvider from './dataProvider'
import devices from './devices'
import emrs from './emrs'
import foods from './foods'
import groupMembers from './group-members'
import groups from './groups'
import HelpPage from './help'
import DocumentationViewer from './help/DocumentationViewer'
import { DEFAULT_LOCALE, localeMessages, resolveLocaleMessages } from './i18n/resolveLocaleMessages'
import { Layout, Login } from './layout'
import leads from './leads'
import metrics from './metrics'
import owners from './owners'
import RegisterOwner from './owners/RegisterOwner'
import pets from './pets'
import { WhatsNewPage } from './releaseNotes'
import { perTabKeyStore } from './store/perTabKeyStore'
import { RA_STORE_APP_KEY } from './storeConfig'
import { softLightTheme } from './themes/softTheme'
import users from './users'
import { queryRetryDelay, shouldRetryQuery } from './utils/queryRetry'
import vetShares from './vet-shares'

function detectBrowserLocale(): string {
  // Derived, not restated: adding a locale to `localeMessages` is enough to
  // make it auto-detectable. A separate hardcoded list here would be a third
  // copy of the locale roster to keep in sync.
  const supported = Object.keys(localeMessages)
  for (const lang of navigator.languages ?? [navigator.language]) {
    const prefix = lang.split('-')[0].toLowerCase()
    if (supported.includes(prefix)) return prefix
  }
  return DEFAULT_LOCALE
}

// Locales are eager-loaded in ./i18n/resolveLocaleMessages. react-admin
// requires the default locale's messages synchronously, and on China the
// auto-detected default is 'zh'; returning a Promise crashes the app before
// render (the spinner never resolves on
// https://www.adapetcn.com/vetpo/#/login). All five bundles together are
// ~150KB, acceptable for an internal admin SPA where plotly alone is ~5MB.
// resolveLocaleMessages layers a partial locale (fr/ja/es) over English so a
// missing key renders the English phrase, not its raw dotted path.
const i18nProvider = polyglotI18nProvider(resolveLocaleMessages, detectBrowserLocale(), [
  { locale: 'en', name: 'English' },
  { locale: 'zh', name: '简体中文' },
  { locale: 'fr', name: 'Français' },
  { locale: 'ja', name: '日本語' },
  { locale: 'es', name: 'Español' },
])

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes - avoid refetching fresh data
      gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache
      refetchOnWindowFocus: false, // Don't refetch on every tab switch
      retry: shouldRetryQuery, // 429: 3 tries w/ long backoff; other 4xx: never; else once
      retryDelay: queryRetryDelay, // 429 backs off 2s/8s/30s so retries can't feed a storm
    },
  },
})

// The app key flows into every persisted key as `RaStore${appKey}.*` — it
// lives in storeConfig so code that must touch raw keys (authProvider
// seeding) derives the same prefix instead of guessing it. Guessing it wrong
// ('RaStore.glucoseUnit' vs 'RaStorevetpo.glucoseUnit') was the deepest root
// cause of the repeat glucose-unit reports.
// `selectedDeviceId` is kept TAB-LOCAL (sessionStorage) via perTabKeyStore so
// two tabs can't fight over one shared, cross-tab-mirrored value — the multi-tab
// "specific sensor reverts to All Devices" thrash (2026-07). Every other key
// stays app-global in localStorage. See src/store/perTabKeyStore.ts.
const store = perTabKeyStore(localStorageStore(undefined, RA_STORE_APP_KEY), ['selectedDeviceId'])

// Set sidebar to be closed by default
store.setItem('sidebar.open', false)

// The two pre-Admin screens (SSO handoff, team-invite acceptance) render
// outside <Admin>, so they never got its theme: MUI-default blue buttons and
// an unstyled body next to a purple portal. Same theme, applied by hand.
const preAdminTheme = createTheme(softLightTheme)

const App = () => {
  // OPS → vet-portal deep-link SSO gate. A fresh load at #/sso?token=...
  // must complete the handoff exchange BEFORE the Admin mounts: the Admin's
  // auth machinery probes GET /me anonymously on boot, and a null-user result
  // fires authProvider.logout() — clearing sessionStorage and killing the
  // session the exchange just stored (observed 2026-07-09: exchange 200 →
  // logout → bounced to /login). SsoLanding rewrites the hash to `next` and
  // flips this flag; the Admin then boots with the session already in place.
  const [ssoInProgress, setSsoInProgress] = useState(() => window.location.hash.startsWith('#/sso'))
  // Clinic-team invite acceptance — same pre-Admin architecture, different
  // reason: the invited account may be a mis-registered role-'user' account
  // that the login role gate would refuse, so acceptance (which promotes it)
  // must happen before any Admin auth machinery exists.
  const [teamInviteInProgress, setTeamInviteInProgress] = useState(() =>
    window.location.hash.startsWith('#/accept-team-invite'),
  )
  if (ssoInProgress) {
    return (
      <ThemeProvider theme={preAdminTheme}>
        <CssBaseline />
        {/* No <Admin> yet, so no translation context: give this page the app's own provider. */}
        <I18nContextProvider value={i18nProvider}>
          <SsoLanding onDone={() => setSsoInProgress(false)} />
        </I18nContextProvider>
      </ThemeProvider>
    )
  }
  if (teamInviteInProgress) {
    return (
      <ThemeProvider theme={preAdminTheme}>
        <CssBaseline />
        <I18nContextProvider value={i18nProvider}>
          <AcceptTeamInvite onDone={() => setTeamInviteInProgress(false)} />
        </I18nContextProvider>
      </ThemeProvider>
    )
  }
  return (
    <Admin
      title="Vet Portal"
      dataProvider={dataProvider}
      store={store}
      authProvider={authProvider}
      queryClient={queryClient}
      dashboard={Dashboard}
      loginPage={Login}
      layout={Layout}
      i18nProvider={i18nProvider}
      disableTelemetry
      theme={softLightTheme}
      requireAuth
    >
      <Resource name="categories" {...categories} />
      <Resource name="activities" {...activities} />
      <Resource name="devices" {...devices} />
      <Resource name="emrs" {...emrs} />
      <Resource name="metrics" {...metrics} />
      <Resource name="pets" {...pets} />
      <Resource name="foods" {...foods} />
      <Resource name="owners" {...owners} />
      <Resource name="leads" {...leads} />
      <Resource name="clinic-team" {...clinicTeam} />
      <Resource name="groups" {...groups} />
      <Resource name="group-members" {...groupMembers} />
      <Resource name="users" {...users} />
      <Resource name="vet-shares" {...vetShares} />
      <CustomRoutes noLayout>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
      </CustomRoutes>
      <CustomRoutes>
        <Route path="/help" element={<HelpPage />} />
        <Route path="/whats-new" element={<WhatsNewPage />} />
        <Route path="/docs/:language/:document" element={<DocumentationViewer />} />
        <Route path="/consent" element={<ConsentRequests />} />
        <Route path="/register-owner" element={<RegisterOwner />} />
      </CustomRoutes>
    </Admin>
  )
}

const AppWrapper = () => (
  <StoreContextProvider value={store}>
    <App />
  </StoreContextProvider>
)

export default AppWrapper
